import axios, { type AxiosInstance } from "axios"
import config from "config"
import type { ICreateLicensePool, ILicensePool, IUpdateLicensePool } from "types/licensePoolTypes"
import type { IAssignLicense, ILicense } from "types/licenseTypes"

import authService from "./authService"

class LicensePoolService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    })
  }

  public createLicensePool = async (
    createLicensePool: ICreateLicensePool,
  ): Promise<ILicensePool> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post<ILicensePool>("/licensepool", createLicensePool, config)

    return data
  }

  public getLicensePools = async (): Promise<Array<ILicensePool>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<ILicensePool>>("/licensepool", config)

    return data
  }

  public getLicensePool = async (id: string): Promise<ILicensePool> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<ILicensePool>(`/licensepool/${id}`, config)

    return data
  }

  public updateLicensePool = async (
    licensePoolId: string,
    updateLicensePool: IUpdateLicensePool,
  ): Promise<ILicensePool> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.put<ILicensePool>(
      `/licensepool/${licensePoolId}`,
      updateLicensePool,
      config,
    )

    return data
  }

  public assignLicense = async (
    licenePoolId: string,
    assignLicense: IAssignLicense,
  ): Promise<ILicense> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post<ILicense>(
      `/licensepool/${licenePoolId}/assign`,
      assignLicense,
      config,
    )

    return data
  }

  public deleteLicensePool = async (licensePoolId: string) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.delete(`/licensepool/${licensePoolId}`, config)

    return resp
  }

  public getOrganizationLicensePools = async (
    organizationId: string,
  ): Promise<Array<ILicensePool>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<ILicensePool>>(
      `/organization/${organizationId}/licensepool`,
      config,
    )

    return data
  }
}

const licensePoolService = new LicensePoolService()

export default licensePoolService
